/**
 * @fileoverview Utilities for working with bigint values and fixed-point math
 */
import { formatUnits } from "viem";

/**
 * RAY precision unit (10^27)
 * Used for high precision fixed-point calculations
 * @type {{bigint: bigint, number: number}}
 */
export const RAY = {
  bigint: BigInt(1e27),
  number: 1e27,
};

/**
 * WAD precision unit (10^18)
 * Common precision unit for Ethereum tokens (matches ETH's 18 decimals)
 * @type {{bigint: bigint, number: number}}
 */
export const WAD = {
  bigint: BigInt(1e18),
  number: 1e18,
};

/**
 * Converts a bigint value to a formatted number string with specified decimal precision
 *
 * @param {bigint} value - The bigint value to convert
 * @param {Object} opts - Formatting options
 * @param {number} [opts.decimals=18] - Number of decimals to use when converting from bigint
 * @param {number} [opts.minimumFractionDigits=0] - Minimum number of fraction digits to display
 * @param {number} [opts.maximumFractionDigits=3] - Maximum number of fraction digits to display
 * @returns {string} The formatted number as a string
 *
 * @example
 * // Returns "123.456"
 * bigIntToNumberAsString(BigInt("123456000000000000000"), { decimals: 18 })
 */
export function bigIntToNumberAsString(
  value: bigint,
  opts: {
    decimals?: number;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {}
): string {
  const {
    decimals = 18,
    minimumFractionDigits = 0,
    maximumFractionDigits = 3,
  } = opts;
  const numberValue = Number.parseFloat(formatUnits(value, decimals));
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: minimumFractionDigits,
    maximumFractionDigits: maximumFractionDigits,
  }).format(numberValue);
}

/**
 * Calculates the expected amount of shares to be minted based on deposit amount and rate
 *
 * The calculation follows the formula:
 * sharesMinted = depositAmount * ONE_SHARE / rateInQuote
 *
 * @param {bigint} depositAmount - The amount to deposit in quote asset decimals
 * @param {bigint} rateInQuote - The rate in quote representing "quote asset per share"
 * @param {number} shareDecimals - The decimal precision of the BoringVault shares (ONE_SHARE)
 * @returns {bigint} The expected amount of shares to be minted
 */
export function calculateExpectedSharesMinted(
  depositAmount: bigint,
  rateInQuote: bigint,
  shareDecimals: number
): bigint {
  // Calculate ONE_SHARE based on the vault's share decimals
  const ONE_SHARE = BigInt(10) ** BigInt(shareDecimals);

  // Calculate shares minted using the formula:
  // sharesMinted = depositAmount * ONE_SHARE / rateInQuote
  const sharesMinted = (depositAmount * ONE_SHARE) / rateInQuote;

  return sharesMinted;
}

/**
 * Calculates the atomic price with slippage applied
 * The calculation follows the formula:
 * atomicPrice = rateInQuote * (1 - slippage)
 *
 * @param {bigint} rateInQuote - The rate in quote representing "quote asset per share"
 * @param {number} slippage - The maximum acceptable slippage as a decimal (e.g., 0.01 for 1%)
 * @param {number} quoteDecimals - The decimal precision of the quote asset
 * @returns {bigint} The atomic price with slippage applied
 */
export function calculateAtomicPrice(
  rateInQuote: bigint,
  slippage: number
): bigint {
  // Convert slippage to basis points (1% = 100 basis points = 0.01)
  // Multiply by 10000 to get precision and subtract from 10000 to get the percentage to keep
  const basisPoints = 10000;
  const slippageInBasisPoints = Math.floor(slippage * basisPoints);
  const percentageToKeep = basisPoints - slippageInBasisPoints;

  // Calculate atomic price using fixed point arithmetic:
  // atomicPrice = rateInQuote * percentageToKeep / basisPoints
  const atomicPrice =
    (rateInQuote * BigInt(percentageToKeep)) / BigInt(basisPoints);

  return atomicPrice;
}
